// pages/DashboardPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package, Truck, AlertTriangle, DollarSign, Clock, CheckCircle,
  TrendingUp, TrendingDown, Eye, Filter, Download, Calendar,
  MapPin, Map, Users, Activity, Zap, Globe, BarChart3,
  PieChart, LineChart, ArrowRight, RefreshCw, Bell, Star, Plus,
  Navigation, Target, Timer, Percent
} from 'lucide-react';
import { Card, Button, Badge, LoadingSpinner } from '../components/ui';

const DashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [realTimeData, setRealTimeData] = useState({});

  // Simulate data loading
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  // Simulate real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      setRealTimeData(prev => ({
        ...prev,
        timestamp: new Date().toLocaleTimeString('en-IN', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        })
      }));
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setRefreshing(false);
  };

  // Enhanced Statistics Data
  const stats = [
    {
      icon: Package,
      title: 'Total Jobs',
      value: '156',
      change: '+12%',
      trend: 'up',
      color: 'blue',
      description: 'Active and completed jobs',
      details: { active: 89, completed: 67 },
      target: 180,
      bgGradient: 'from-blue-500 to-blue-600'
    },
    {
      icon: Truck,
      title: 'In Transit',
      value: '24',
      change: '+8%',
      trend: 'up',
      color: 'emerald',
      description: 'Currently moving vehicles',
      details: { onTime: 20, delayed: 4 },
      target: 30,
      bgGradient: 'from-emerald-500 to-emerald-600'
    },
    {
      icon: AlertTriangle,
      title: 'Delayed',
      value: '3',
      change: '-25%',
      trend: 'down',
      color: 'red',
      description: 'Behind schedule',
      details: { minor: 2, major: 1 },
      target: 0,
      bgGradient: 'from-red-500 to-red-600'
    },
    {
      icon: DollarSign,
      title: 'Revenue',
      value: '₹2.4L',
      change: '+15%',
      trend: 'up',
      color: 'green',
      description: 'This month earnings',
      details: { thisWeek: '₹65K', lastWeek: '₹58K' },
      target: '₹3L',
      bgGradient: 'from-green-500 to-green-600'
    }
  ];

  // Enhanced Jobs Data
  const activeJobs = [
    {
      id: 'JOB001',
      container: 'CONT123456',
      type: 'Import',
      route: 'Mumbai Port → Pune WH',
      customer: 'Tata Motors',
      status: 'in_transit',
      progress: 65,
      eta: '2 hours',
      driver: 'Rajesh Kumar',
      vehicle: 'MH-01-AB-1234',
      priority: 'high',
      detentionRisk: 'low',
      currentLocation: 'Lonavala Toll Plaza',
      distance: { completed: 120, total: 185 },
      estimatedCost: '₹15,000',
      actualCost: '₹12,500'
    },
    {
      id: 'JOB002',
      container: 'CONT789012',
      type: 'Export',
      route: 'Delhi WH → Customer Warehouse',
      customer: 'Maruti Suzuki',
      status: 'loading',
      progress: 15,
      eta: '4 hours',
      driver: 'Suresh Yadav',
      vehicle: 'DL-02-XY-9876',
      priority: 'medium',
      detentionRisk: 'medium',
      currentLocation: 'Delhi Warehouse',
      distance: { completed: 0, total: 95 },
      estimatedCost: '₹8,500',
      actualCost: '₹8,500'
    },
    {
      id: 'JOB003',
      container: 'CONT345678',
      type: 'Domestic',
      route: 'Chennai Port → Bangalore WH',
      customer: 'Infosys Limited',
      status: 'delayed',
      progress: 45,
      eta: '6 hours',
      driver: 'Murugan S',
      vehicle: 'TN-09-BC-5432',
      priority: 'high',
      detentionRisk: 'high',
      currentLocation: 'Hosur Bypass',
      distance: { completed: 155, total: 345 },
      estimatedCost: '₹22,000',
      actualCost: '₹25,000'
    },
    {
      id: 'JOB004',
      container: 'CONT901234',
      type: 'Import',
      route: 'Kolkata Port → Vendor WH',
      customer: 'Coal India',
      status: 'completed',
      progress: 100,
      eta: 'Delivered',
      driver: 'Amit Ghosh',
      vehicle: 'WB-03-CD-7890',
      priority: 'low',
      detentionRisk: 'none',
      currentLocation: 'Delivered',
      distance: { completed: 420, total: 420 },
      estimatedCost: '₹18,000',
      actualCost: '₹17,500'
    }
  ];

  // Performance metrics
  const performanceMetrics = [
    {
      label: 'On-Time Delivery',
      value: 94,
      target: 95,
      color: 'emerald',
      icon: Timer,
      description: 'Deliveries completed on schedule'
    },
    {
      label: 'Cost Efficiency',
      value: 87,
      target: 90,
      color: 'blue',
      icon: Target,
      description: 'Cost savings vs budget'
    },
    {
      label: 'Customer Satisfaction',
      value: 92,
      target: 95,
      color: 'purple',
      icon: Star,
      description: 'Customer feedback rating'
    },
    {
      label: 'Vehicle Utilization',
      value: 78,
      target: 85,
      color: 'orange',
      icon: Percent,
      description: 'Fleet capacity utilization'
    }
  ];

  // Recent alerts
  const alerts = [
    {
      id: 1,
      type: 'warning',
      title: 'Traffic Delay Alert',
      message: 'Job JOB003 delayed by 2 hours due to heavy traffic on NH-44',
      time: '5 mins ago',
      severity: 'medium',
      action: 'View Details',
      jobId: 'JOB003'
    },
    {
      id: 2,
      type: 'success',
      title: 'Delivery Completed',
      message: 'Container CONT901234 delivered successfully to Coal India warehouse',
      time: '15 mins ago',
      severity: 'low',
      action: 'Generate Invoice',
      jobId: 'JOB004'
    },
    {
      id: 3,
      type: 'error',
      title: 'GPS Signal Lost',
      message: 'Vehicle MH-02-CD-5678 GPS signal lost near Nashik highway',
      time: '25 mins ago',
      severity: 'high',
      action: 'Contact Driver',
      jobId: null
    },
    {
      id: 4,
      type: 'info',
      title: 'Free Time Warning',
      message: 'Container CONT345678 approaching free time limit (2 hours remaining)',
      time: '45 mins ago',
      severity: 'medium',
      action: 'Expedite Delivery',
      jobId: 'JOB003'
    }
  ];

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  const StatCard = ({ icon: Icon, title, value, change, trend, color, description, details, target, bgGradient }) => {
    const numericValue = parseInt(value.replace(/[^0-9]/g, '')) || 0;
    const numericTarget = typeof target === 'string' ? parseInt(target.replace(/[^0-9]/g, '')) : target || 100;
    const progressPercentage = Math.min((numericValue / numericTarget) * 100, 100);

    return (
      <Card hover className="relative overflow-hidden group transition-all duration-300 hover:shadow-xl">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className={`p-3 rounded-xl bg-gradient-to-r ${bgGradient} shadow-lg group-hover:scale-110 transition-transform duration-300`}>
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <div className={`flex items-center text-sm font-medium transition-colors duration-200 ${trend === 'up' ? 'text-emerald-600' : 'text-red-600'
                }`}>
                {trend === 'up' ? (
                  <TrendingUp className="w-4 h-4 mr-1" />
                ) : (
                  <TrendingDown className="w-4 h-4 mr-1" />
                )}
                {change}
              </div>
            </div>
          </div>

          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-600 mb-1">{title}</h3>
            <p className="text-3xl font-bold text-gray-900 mb-1">{value}</p>
            <p className="text-xs text-gray-500">{description}</p>
          </div>

          {/* Progress to target */}
          {target && (
            <div className="mb-4">
              <div className="flex justify-between text-xs text-gray-500 mb-2">
                <span>Progress to target</span>
                <span>{target}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-2 rounded-full bg-gradient-to-r ${bgGradient} transition-all duration-1000 ease-out`}
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
          )}

          {/* Additional details */}
          {details && (
            <div className="pt-4 border-t border-gray-100">
              <div className="grid grid-cols-2 gap-3 text-xs">
                {Object.entries(details).map(([key, val]) => (
                  <div key={key} className="flex justify-between items-center">
                    <span className="text-gray-500 capitalize">{key}:</span>
                    <span className="font-medium text-gray-700">{val}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Card>
    );
  };

  const JobRow = ({ job }) => {
    const statusConfig = {
      in_transit: {
        badge: 'primary',
        icon: Truck,
        text: 'In Transit',
        bgColor: 'bg-blue-50',
        textColor: 'text-blue-700'
      },
      loading: {
        badge: 'warning',
        icon: Clock,
        text: 'Loading',
        bgColor: 'bg-yellow-50',
        textColor: 'text-yellow-700'
      },
      delayed: {
        badge: 'danger',
        icon: AlertTriangle,
        text: 'Delayed',
        bgColor: 'bg-red-50',
        textColor: 'text-red-700'
      },
      completed: {
        badge: 'success',
        icon: CheckCircle,
        text: 'Completed',
        bgColor: 'bg-emerald-50',
        textColor: 'text-emerald-700'
      }
    };

    const priorityConfig = {
      high: { color: 'red', label: 'High', bgColor: 'bg-red-100', textColor: 'text-red-800' },
      medium: { color: 'yellow', label: 'Medium', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' },
      low: { color: 'green', label: 'Low', bgColor: 'bg-green-100', textColor: 'text-green-800' }
    };

    const config = statusConfig[job.status];
    const StatusIcon = config.icon;
    const priority = priorityConfig[job.priority];

    return (
      <tr className="hover:bg-gray-50/80 transition-all duration-200 cursor-pointer group" onClick={() => navigate(`/jobs/${job.id}`)}>
        <td className="px-6 py-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow duration-200">
                <Package className="w-5 h-5 text-white" />
              </div>
            </div>
            <div className="ml-3">
              <div className="flex items-center space-x-2">
                <span className="font-semibold text-blue-600 hover:text-blue-700 transition-colors">{job.id}</span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priority.bgColor} ${priority.textColor}`}>
                  {priority.label}
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-1">{job.type}</p>
            </div>
          </div>
        </td>

        <td className="px-6 py-4">
          <div>
            <p className="font-medium text-gray-900">{job.container}</p>
            <p className="text-sm text-gray-500 mt-1">{job.customer}</p>
          </div>
        </td>

        <td className="px-6 py-4">
          <div className="flex items-start space-x-2">
            <Navigation className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{job.route}</p>
              <p className="text-xs text-gray-500 flex items-center mt-1">
                <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                {job.currentLocation}
              </p>
            </div>
          </div>
        </td>

        <td className="px-6 py-4">
          <div className="space-y-2">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}>
              <StatusIcon className="w-3 h-3 mr-1.5" />
              {config.text}
            </span>
            {job.detentionRisk === 'high' && (
              <div>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  Detention Risk
                </span>
              </div>
            )}
          </div>
        </td>

        <td className="px-6 py-4">
          <div className="space-y-2">
            <div className="flex items-center">
              <div className="w-24 bg-gray-200 rounded-full h-2.5 mr-3 overflow-hidden">
                <div
                  className={`h-2.5 rounded-full transition-all duration-500 ${job.progress >= 100 ? 'bg-emerald-500' :
                      job.progress >= 70 ? 'bg-blue-500' :
                        job.progress >= 40 ? 'bg-yellow-500' : 'bg-gray-400'
                    }`}
                  style={{ width: `${job.progress}%` }}
                />
              </div>
              <span className="text-sm font-medium text-gray-700 min-w-[3rem]">{job.progress}%</span>
            </div>
            <p className="text-xs text-gray-500">
              {job.distance.completed}km / {job.distance.total}km
            </p>
          </div>
        </td>

        <td className="px-6 py-4">
          <div className="text-right space-y-1">
            <p className="text-sm font-medium text-gray-900">{job.eta}</p>
            <p className="text-xs text-gray-600">{job.driver}</p>
            <p className="text-xs text-gray-500">{job.vehicle}</p>
          </div>
        </td>

        <td className="px-6 py-4">
          <div className="text-right space-y-1">
            <p className="text-sm font-semibold text-gray-900">{job.actualCost}</p>
            <p className="text-xs text-gray-500">Est: {job.estimatedCost}</p>
            {job.actualCost !== job.estimatedCost && (
              <p className={`text-xs font-medium ${parseInt(job.actualCost.replace(/[^0-9]/g, '')) <= parseInt(job.estimatedCost.replace(/[^0-9]/g, ''))
                  ? 'text-emerald-600' : 'text-red-600'
                }`}>
                {parseInt(job.actualCost.replace(/[^0-9]/g, '')) <= parseInt(job.estimatedCost.replace(/[^0-9]/g, ''))
                  ? 'Under budget' : 'Over budget'}
              </p>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const PerformanceChart = () => (
    <Card className="h-full">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Performance Metrics</h3>
            <p className="text-sm text-gray-500 mt-1">Key operational indicators</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/analytics')}>
            <BarChart3 className="w-4 h-4 mr-2" />
            View Details
          </Button>
        </div>

        <div className="space-y-6">
          {performanceMetrics.map((metric, index) => {
            const IconComponent = metric.icon;
            const isAboveTarget = metric.value >= metric.target;

            return (
              <div key={index} className="group">
                <div className="flex justify-between items-center mb-3">
                  <div className="flex items-center space-x-2">
                    <IconComponent className={`w-4 h-4 text-${metric.color}-500`} />
                    <span className="text-sm font-medium text-gray-700">{metric.label}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-xs text-gray-500">Target: {metric.target}%</span>
                    <span className={`text-sm font-semibold ${isAboveTarget ? 'text-emerald-600' : 'text-red-600'
                      }`}>
                      {metric.value}%
                    </span>
                  </div>
                </div>

                <div className="relative">
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-3 rounded-full bg-gradient-to-r from-${metric.color}-400 to-${metric.color}-600 transition-all duration-1000 ease-out`}
                      style={{ width: `${metric.value}%` }}
                    />
                  </div>
                  {/* Target indicator */}
                  <div
                    className="absolute top-0 w-0.5 h-3 bg-gray-700 rounded-full"
                    style={{ left: `${metric.target}%` }}
                  />
                </div>

                <p className="text-xs text-gray-500 mt-2">{metric.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );

  const AlertsPanel = () => (
    <Card className="h-full">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Bell className="w-5 h-5 mr-2 text-red-500" />
              Recent Alerts
            </h3>
            <p className="text-sm text-gray-500 mt-1">Real-time system notifications</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/alerts')}>
            View All
          </Button>
        </div>

        <div className="space-y-4 max-h-80 overflow-y-auto">
          {alerts.map((alert) => {
            const alertConfig = {
              warning: {
                icon: Clock,
                bgColor: 'bg-yellow-50',
                iconColor: 'text-yellow-600',
                borderColor: 'border-yellow-200',
                iconBg: 'bg-yellow-100'
              },
              success: {
                icon: CheckCircle,
                bgColor: 'bg-emerald-50',
                iconColor: 'text-emerald-600',
                borderColor: 'border-emerald-200',
                iconBg: 'bg-emerald-100'
              },
              error: {
                icon: AlertTriangle,
                bgColor: 'bg-red-50',
                iconColor: 'text-red-600',
                borderColor: 'border-red-200',
                iconBg: 'bg-red-100'
              },
              info: {
                icon: Activity,
                bgColor: 'bg-blue-50',
                iconColor: 'text-blue-600',
                borderColor: 'border-blue-200',
                iconBg: 'bg-blue-100'
              }
            };

            const config = alertConfig[alert.type];
            const AlertIcon = config.icon;

            return (
              <div key={alert.id} className={`p-4 rounded-xl border-l-4 ${config.bgColor} ${config.borderColor} hover:shadow-sm transition-shadow duration-200`}>
                <div className="flex items-start space-x-3">
                  <div className={`p-2 rounded-lg ${config.iconBg} ${config.iconColor} flex-shrink-0`}>
                    <AlertIcon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-gray-900 truncate">{alert.title}</h4>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${alert.severity === 'high' ? 'bg-red-100 text-red-800' :
                          alert.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                        }`}>
                        {alert.severity}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{alert.message}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">{alert.time}</span>
                      <Button
                        variant="outline"
                        size="xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (alert.jobId) {
                            navigate(`/jobs/${alert.jobId}`);
                          }
                        }}
                      >
                        {alert.action}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );

  const QuickStatsCards = () => (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      {[
        { icon: Users, value: '42', label: 'Active Drivers', color: 'blue', bgGradient: 'from-blue-500 to-blue-600' },
        { icon: Globe, value: '8', label: 'Cities Covered', color: 'emerald', bgGradient: 'from-emerald-500 to-emerald-600' },
        { icon: Zap, value: '94%', label: 'Efficiency', color: 'yellow', bgGradient: 'from-yellow-500 to-yellow-600' },
        { icon: Star, value: '4.8', label: 'Customer Rating', color: 'purple', bgGradient: 'from-purple-500 to-purple-600' }
      ].map((stat, index) => {
        const IconComponent = stat.icon;
        return (
          <Card key={index} className="p-4 text-center group hover:shadow-lg transition-all duration-300">
            <div className={`w-12 h-12 bg-gradient-to-r ${stat.bgGradient} rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
              <IconComponent className="w-6 h-6 text-white" />
            </div>
            <p className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</p>
            <p className="text-sm text-gray-600">{stat.label}</p>
          </Card>
        );
      })}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="p-4 sm:p-6 space-y-6">
        {/* Page Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center">
              Welcome back, John!
              <Zap className="w-6 h-6 text-yellow-500 ml-2 animate-pulse" />
            </h1>
            <p className="text-gray-600 mt-2 text-sm sm:text-base">
              Here's what's happening with your logistics operations today.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="px-8 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-sm"
            >
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
            </select>

            <Button variant="outline" size="sm" onClick={handleRefresh} loading={refreshing}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>

            <Button variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        {/* Real-time indicator */}
        {realTimeData.timestamp && (
          <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 border border-emerald-200 rounded-xl p-4">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse mr-3" />
              <span className="text-sm text-emerald-700 font-medium">
                Live data - Last updated: {realTimeData.timestamp}
              </span>
            </div>
          </div>
        )}

        {/* Main Statistics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {stats.map((stat, index) => (
            <StatCard key={index} {...stat} />
          ))}
        </div>

        {/* Quick Stats */}
        <QuickStatsCards />

        {/* Charts and Tables Row */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-1">
            <PerformanceChart />
          </div>
          <div className="xl:col-span-2">
            <AlertsPanel />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;